import { HEADER_BYTES, MALFORMED, OK, assertField, checkHeader, putHeader, putU16, readU16, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16 } from "./limits.ts";

/**
 * The `effects` frame (ADR-079 decision 9, PROTOCOL.md 5.12): server to client, the effects the player's own character
 * bears, visible to watchers or not, each with its number (its place in the rules' effects, counting from 1), its ticks
 * left counted from the tick it is sent (as `used` counts a cooldown, since a binary header carries no tick) and its
 * stacks. It reaches only its own player, with the first snapshot and whenever the set, a time left or a stack changed;
 * watchers read the Buffs component. A map server writes it into a buffer its session allocates at join
 * (`putEffectEntry`, `putEffectsHeader`), so a send allocates nothing; the decoder never throws (ADR-030).
 */

/**
 * Bytes before the first effect: the frame header and the count.
 */
export const EFFECTS_HEADER_BYTES = HEADER_BYTES + 1;

/**
 * Bytes of one effect: its number, its ticks left and its stacks.
 */
export const EFFECT_ENTRY_BYTES = 5;

const MAX_COUNT = 255;
const MAX_BYTE = 255;

/**
 * One effect, decoded or to be encoded.
 */
export interface EffectEntry {
  effect: number;
  ticksLeft: number;
  stacks: number;
}

/**
 * A decoded `effects` frame, reused across frames: how many effects it held and the effects, the first `count` of
 * `effects` valid.
 */
export interface EffectsRecord {
  count: number;
  readonly effects: EffectEntry[];
}

/**
 * A fresh record for `decodeEffects` to fill.
 *
 * @returns An empty record.
 */
export function newEffectsRecord(): EffectsRecord {
  return { count: 0, effects: [] };
}

/**
 * Bytes of a frame of a number of effects.
 *
 * @param count The effects.
 * @returns `4 + 5 * count`.
 */
export function effectsBytes(count: number): number {
  return EFFECTS_HEADER_BYTES + EFFECT_ENTRY_BYTES * count;
}

/**
 * Writes one effect at its place in a frame through plain byte stores. The caller has sized the buffer and checked the
 * fields' widths, as a map server does with values it holds in typed arrays of those widths.
 *
 * @param buffer The frame.
 * @param index The effect's place, from 0.
 * @param effect The effect's number.
 * @param ticksLeft Its ticks left.
 * @param stacks Its stacks.
 */
export function putEffectEntry(buffer: Uint8Array, index: number, effect: number, ticksLeft: number, stacks: number): void {
  const at = EFFECTS_HEADER_BYTES + EFFECT_ENTRY_BYTES * index;
  putU16(buffer, at, effect);
  putU16(buffer, at + 2, ticksLeft);
  buffer[at + 4] = stacks;
}

/**
 * Writes a frame's header once its effects are in place.
 *
 * @param buffer The frame.
 * @param count The effects written, 0 to 255.
 * @returns The frame's length in bytes.
 */
export function putEffectsHeader(buffer: Uint8Array, count: number): number {
  putHeader(buffer, MessageType.Effects);
  buffer[HEADER_BYTES] = count;
  return effectsBytes(count);
}

/**
 * Encodes an `effects` frame into a caller-owned buffer, checking every field.
 *
 * @param buffer Destination, at least `effectsBytes(effects.length)` long.
 * @param effects 0 to 255 effects.
 * @returns Bytes written.
 * @throws Error when there are more than 255, the buffer is too small, or a field does not fit its width.
 */
export function encodeEffects(buffer: Uint8Array, effects: readonly Readonly<EffectEntry>[]): number {
  if (effects.length > MAX_COUNT) {
    throw new Error(`effect count ${effects.length} is more than ${MAX_COUNT}`);
  }
  const length = effectsBytes(effects.length);
  if (buffer.length < length) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${length}-byte effects frame`);
  }
  for (let index = 0; index < effects.length; index += 1) {
    const entry = effects[index] as Readonly<EffectEntry>;
    assertField(entry.effect, MAX_U16, `effects effect ${entry.effect}`);
    assertField(entry.ticksLeft, MAX_U16, `effects ticksLeft ${entry.ticksLeft}`);
    assertField(entry.stacks, MAX_BYTE, `effects stacks ${entry.stacks}`);
    putEffectEntry(buffer, index, entry.effect, entry.ticksLeft, entry.stacks);
  }
  return putEffectsHeader(buffer, effects.length);
}

/**
 * Decodes an `effects` frame into a record. Never throws: a frame shorter than its header, a length other than
 * `4 + 5 * count` or another type is malformed, and another version a mismatch, leaving the record untouched. Every
 * value of every field is well formed.
 *
 * @param frame The whole frame.
 * @param out Record to fill.
 * @returns The verdict.
 */
export function decodeEffects(frame: Uint8Array, out: EffectsRecord): DecodeVerdict {
  if (frame.length < EFFECTS_HEADER_BYTES) {
    return MALFORMED;
  }
  const count = frame[HEADER_BYTES] as number;
  const header = checkHeader(frame, MessageType.Effects, effectsBytes(count));
  if (header.kind !== "ok") {
    return header;
  }
  const effects = out.effects;
  for (let index = 0; index < count; index += 1) {
    const at = EFFECTS_HEADER_BYTES + EFFECT_ENTRY_BYTES * index;
    let entry = effects[index];
    if (entry === undefined) {
      entry = { effect: 0, ticksLeft: 0, stacks: 0 };
      effects.push(entry);
    }
    entry.effect = readU16(frame, at);
    entry.ticksLeft = readU16(frame, at + 2);
    entry.stacks = frame[at + 4] as number;
  }
  out.count = count;
  return OK;
}
